/*
 * Review posts with video (sections/lun-reviews.liquid, snippets/lun-review-card.liquid).
 *
 * A video plays muted and looped only while its card is mostly on screen, like the customer videos did.
 * The round button pauses or resumes it; a video the visitor paused stays paused. With reduced motion
 * nothing starts by itself: the button is the only way to play.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {IntersectionObserver | undefined} */
let observer;

/**
 * @param {HTMLElement} media
 * @returns {HTMLVideoElement | null}
 */
function videoOf(media) {
  return media.querySelector('video');
}

/**
 * @param {HTMLElement} media
 * @param {boolean} playing
 */
function showState(media, playing) {
  media.dataset.playing = String(playing);
  const button = media.querySelector('[data-lun-rev-toggle]');
  if (!button) return;
  button.setAttribute('aria-label', (playing ? media.dataset.labelPause : media.dataset.labelPlay) || '');
}

/**
 * @param {HTMLElement} media
 */
async function play(media) {
  const video = videoOf(media);
  if (!video) return;
  try {
    await video.play();
    showState(media, true);
  } catch {
    showState(media, false); // autoplay refused: the button still works
  }
}

/**
 * @param {HTMLElement} media
 */
function pause(media) {
  videoOf(media)?.pause();
  showState(media, false);
}

/**
 * @param {HTMLElement} media
 */
function setup(media) {
  if (media.dataset.lunReady === 'true') return;
  media.dataset.lunReady = 'true';
  showState(media, false);

  media.querySelector('[data-lun-rev-toggle]')?.addEventListener('click', () => {
    const playing = media.dataset.playing === 'true';
    media.dataset.userPaused = String(playing);
    if (playing) pause(media);
    else play(media);
  });

  observer?.observe(media);
}

const MAX_LINES = 4;

/**
 * Shows the text in at most MAX_LINES lines with "… Read more" at the end of the last line. The button sits inline
 * inside the paragraph, so the text is cut by measuring: the longest start of the text that still fits together
 * with the ellipsis and the button. The full text is kept in data-full. Without JS the whole text shows.
 * @param {HTMLElement} body
 */
function measureBody(body) {
  const text = /** @type {HTMLElement | null} */ (body.querySelector('[data-lun-rev-text]'));
  const button = /** @type {HTMLButtonElement | null} */ (body.querySelector('[data-lun-rev-more]'));
  if (!text || !button || body.dataset.expanded === 'true') return;

  const full = text.dataset.full || text.textContent || '';
  text.dataset.full = full;

  const lineHeight = Number.parseFloat(getComputedStyle(text).lineHeight) || Number.parseFloat(getComputedStyle(text).fontSize) * 1.55;
  const limit = lineHeight * MAX_LINES + 1;
  const fits = () => text.offsetHeight <= limit;

  // Park the button outside the paragraph: setting textContent would drop it otherwise
  body.append(button);
  button.hidden = true;
  text.textContent = full;
  if (fits()) return;

  button.hidden = false;
  /** @param {number} length */
  const show = (length) => {
    body.append(button);
    text.textContent = `${full.slice(0, length).trimEnd()}… `;
    text.append(button);
  };

  let low = 0;
  let high = full.length;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    show(middle);
    if (fits()) low = middle;
    else high = middle - 1;
  }
  // Do not cut a word in half
  const space = full.lastIndexOf(' ', low);
  show(space > 0 && low < full.length ? space : low);
  button.textContent = button.dataset.labelMore || '';
}

/**
 * @param {HTMLElement} body
 */
function setupBody(body) {
  if (body.dataset.lunReady === 'true') return;
  body.dataset.lunReady = 'true';

  const text = /** @type {HTMLElement | null} */ (body.querySelector('[data-lun-rev-text]'));
  const button = /** @type {HTMLButtonElement | null} */ (body.querySelector('[data-lun-rev-more]'));
  button?.addEventListener('click', () => {
    if (!text) return;
    const expand = body.dataset.expanded !== 'true';
    body.dataset.expanded = String(expand);
    button.setAttribute('aria-expanded', String(expand));
    if (expand) {
      body.append(button);
      text.textContent = `${text.dataset.full || ''} `;
      text.append(button);
      button.textContent = button.dataset.labelLess || '';
    } else {
      measureBody(body);
    }
  });

  measureBody(body);
}

/**
 * When every card fits in the row the cards are centred by CSS; the heading follows (data-fits).
 * @param {Element} section
 */
function measureSection(section) {
  const row = section.querySelector('.lun-rev__row');
  if (!row) return;
  section.toggleAttribute('data-fits', row.scrollWidth <= row.clientWidth + 1);
}

/** @type {ResizeObserver | undefined} */
let resizer;

function measureAll() {
  for (const body of document.querySelectorAll('[data-lun-rev-body]')) measureBody(/** @type {HTMLElement} */ (body));
  for (const section of document.querySelectorAll('.lun-rev')) measureSection(section);
}

function boot() {
  if (!resizer && 'ResizeObserver' in window) {
    resizer = new ResizeObserver(() => measureAll());
  }
  for (const row of document.querySelectorAll('.lun-rev__row')) resizer?.observe(row);
  for (const body of document.querySelectorAll('[data-lun-rev-body]')) setupBody(/** @type {HTMLElement} */ (body));
  for (const section of document.querySelectorAll('.lun-rev')) measureSection(section);
  // Web fonts change how the text wraps
  document.fonts?.ready.then(measureAll);

  if (!observer && 'IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const media = /** @type {HTMLElement} */ (entry.target);
          if (entry.isIntersecting && !REDUCED_MOTION.matches && media.dataset.userPaused !== 'true') play(media);
          else if (!entry.isIntersecting) pause(media);
        }
      },
      { threshold: 0.6 }
    );
  }

  for (const media of document.querySelectorAll('[data-lun-rev-video]')) setup(/** @type {HTMLElement} */ (media));
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
document.addEventListener('shopify:section:load', boot);
